import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import Pagination from "@/components/common/Pagination";
import {
  WindProviderService,
  PaginatedResponse,
  SpotWindEntry,
  CountryWindEntry,
  WindProviderConfig,
  WindStats,
  WindModelPreview,
  WindModelOption,
} from "@/services/WindProviderService";
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  ChevronDownIcon,
  EyeIcon,
  GlobeAltIcon,
  MapPinIcon,
  FlagIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";

// --- Helpers ---

function countryFlag(alpha2: string): string {
  if (!alpha2 || alpha2.length !== 2) return "";
  return String.fromCodePoint(
    ...alpha2
      .toUpperCase()
      .split("")
      .map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
}

type TabId = "dashboard" | "spot" | "country" | "global";

// --- Config Modal ---

interface ConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: WindProviderConfig) => void;
  config: WindProviderConfig | null;
  providers: any[];
  mode: "spot" | "country" | "global";
  entityName?: string;
  latitude?: number;
  longitude?: number;
}

function ConfigModal({
  isOpen,
  onClose,
  onSave,
  config,
  providers,
  mode,
  entityName,
  latitude,
  longitude,
}: ConfigModalProps) {
  const [form, setForm] = useState<WindProviderConfig>({
    provider: "",
    dataType: "forecast",
    enabled: true,
    priority: 0,
  });
  const [availableModels, setAvailableModels] = useState<WindModelOption[]>([]);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const modelDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (config) {
      setForm(config);
    } else {
      setForm({
        provider: "",
        dataType: "forecast",
        enabled: true,
        priority: 0,
        modelId: "",
      });
    }
  }, [config]);

  // Load available models based on location
  useEffect(() => {
    if (!isOpen) return;
    WindProviderService.getAvailableModels(latitude, longitude)
      .then(setAvailableModels)
      .catch(() => setAvailableModels([]));
  }, [isOpen, latitude, longitude]);

  // Close model dropdown on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target as Node)) {
        setModelDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleProviderChange = (provider: string) => {
    setForm((prev) => ({
      ...prev,
      provider,
      modelId: "",
      // Open-Meteo only supports forecast — auto-correct the data type
      ...(provider === "openmeteo" && prev.dataType === "live" ? { dataType: "forecast" } : {}),
    }));
  };

  const handleModelSelect = (modelId: string) => {
    setForm((prev) => ({ ...prev, modelId }));
    setModelDropdownOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">
            {config?.id ? "Edit" : "Add"} Provider Config
            {entityName && (
              <span className="text-gray-400 font-normal ml-2">
                - {entityName}
              </span>
            )}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Provider */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Provider
            </label>
            <select
              value={form.provider}
              onChange={(e) => handleProviderChange(e.target.value)}
              required
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select provider...</option>
              <option value="openmeteo">Open-Meteo (Forecast)</option>
              <option value="windguru">Windguru (Live + Forecast)</option>
            </select>
          </div>

          {/* Model — combobox with available models */}
          <div className="relative" ref={modelDropdownRef}>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Forecast Model
            </label>
            <input
              type="text"
              value={form.modelId || ""}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, modelId: e.target.value }));
                setModelDropdownOpen(true);
              }}
              onFocus={() => setModelDropdownOpen(true)}
              placeholder="Type or select model (empty = auto-select)"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoComplete="off"
            />
            {modelDropdownOpen && availableModels.length > 0 && (
              <div className="absolute z-30 left-0 right-0 mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-xl max-h-60 overflow-auto">
                {availableModels.filter(m => m.available).length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-emerald-400 bg-gray-750 border-b border-gray-600 font-semibold">
                      Available for this location
                    </div>
                    {availableModels
                      .filter(m => m.available)
                      .filter(m => !form.modelId || m.modelId.includes(form.modelId) || m.modelName.toLowerCase().includes((form.modelId || "").toLowerCase()))
                      .map(m => (
                        <button
                          key={m.modelId}
                          type="button"
                          onClick={() => handleModelSelect(m.modelId)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-600 transition-colors flex items-center justify-between"
                        >
                          <span className="text-white">{m.modelName} <span className="text-gray-400">({m.resolution})</span></span>
                          <span className="text-xs text-gray-500 font-mono">{m.modelId}</span>
                        </button>
                      ))}
                  </>
                )}
                {availableModels.filter(m => !m.available).length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-750 border-b border-gray-600 border-t font-semibold">
                      Other regions
                    </div>
                    {availableModels
                      .filter(m => !m.available)
                      .filter(m => !form.modelId || m.modelId.includes(form.modelId) || m.modelName.toLowerCase().includes((form.modelId || "").toLowerCase()))
                      .map(m => (
                        <button
                          key={m.modelId}
                          type="button"
                          onClick={() => handleModelSelect(m.modelId)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-600 transition-colors flex items-center justify-between opacity-50"
                        >
                          <span className="text-gray-300">{m.modelName} <span className="text-gray-500">({m.resolution})</span></span>
                          <span className="text-xs text-gray-600 font-mono">{m.modelId}</span>
                        </button>
                      ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Type
            </label>
            <select
              value={form.dataType}
              onChange={(e) => {
                const dataType = e.target.value as "forecast" | "live";
                setForm((prev) => ({
                  ...prev,
                  dataType,
                  // Live data is only supported by Windguru — auto-correct the provider
                  ...(dataType === "live" ? { provider: "windguru" } : {}),
                }));
              }}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="forecast">Forecast</option>
              <option value="live" disabled={form.provider === "openmeteo"}>Live{form.provider === "openmeteo" ? " (Windguru only)" : ""}</option>
            </select>
          </div>

          {/* Station ID (for live) */}
          {form.dataType === "live" && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Station ID
              </label>
              <input
                type="text"
                value={form.stationId || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, stationId: e.target.value }))
                }
                placeholder="e.g. WG12345"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
              />
            </div>
          )}

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Priority
            </label>
            <input
              type="number"
              value={form.priority ?? 0}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  priority: parseInt(e.target.value) || 0,
                }))
              }
              min={0}
              max={100}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Higher priority configs are used first (0 = lowest)
            </p>
          </div>

          {/* Enabled */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="config-enabled"
              checked={form.enabled}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, enabled: e.target.checked }))
              }
              className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
            />
            <label
              htmlFor="config-enabled"
              className="text-sm text-gray-300"
            >
              Enabled
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!form.provider}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
            >
              {config?.id ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Wind Preview Panel ---

interface PreviewPanelProps {
  preview: any | null;
  loading: boolean;
  onClose: () => void;
}

function PreviewPanel({ preview, loading, onClose }: PreviewPanelProps) {
  if (!preview && !loading) return null;

  return (
    <div className="fixed right-0 top-0 bottom-0 w-96 bg-gray-800 border-l border-gray-700 shadow-2xl z-40 overflow-y-auto">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
        <h3 className="text-sm font-semibold text-white">Wind Preview</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin" />
            <span className="ml-2 text-gray-400 text-sm">
              Loading preview...
            </span>
          </div>
        ) : preview ? (
          <div className="space-y-3">
            <div className="text-xs text-gray-400">
              Provider: {preview.provider}
              {preview.model && ` / ${preview.model}`}
            </div>
            {preview.timestamp && (
              <div className="text-xs text-gray-500">
                Updated: {new Date(preview.timestamp).toLocaleString()}
              </div>
            )}
            <pre className="text-xs text-gray-300 bg-gray-900 rounded-lg p-3 overflow-x-auto max-h-96">
              {JSON.stringify(preview.data, null, 2)}
            </pre>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 text-sm">
            No preview data available
          </div>
        )}
      </div>
    </div>
  );
}

// --- Chevron Icon ---

function ChevronIcon({ rotated }: { rotated: boolean }) {
  return (
    <ChevronDownIcon
      className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
        rotated ? "rotate-180" : ""
      }`}
    />
  );
}

// --- Inline Config Cards ---

interface ConfigCardsProps {
  configs: WindProviderConfig[];
  loading: boolean;
  onAdd: () => void;
  onEdit: (config: WindProviderConfig) => void;
  onDelete: (config: WindProviderConfig) => void;
  onToggle: (config: WindProviderConfig) => void;
  onPreview?: (config: WindProviderConfig) => void;
}

function ConfigCards({
  configs,
  loading,
  onAdd,
  onEdit,
  onDelete,
  onToggle,
  onPreview,
}: ConfigCardsProps) {
  if (loading) {
    return (
      <div className="px-6 py-4 flex items-center gap-2">
        <div className="w-4 h-4 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin" />
        <span className="text-sm text-gray-400">Loading configs...</span>
      </div>
    );
  }

  return (
    <div className="px-6 py-4 bg-gray-850 space-y-3">
      {configs.length === 0 ? (
        <div className="text-sm text-gray-500 italic">
          No provider configs. Using global defaults.
        </div>
      ) : (
        <div className="grid gap-2">
          {configs.map((cfg) => (
            <div
              key={cfg.id}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                cfg.enabled
                  ? "bg-gray-700/50 border-gray-600"
                  : "bg-gray-800/50 border-gray-700 opacity-60"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <button
                  onClick={() => onToggle(cfg)}
                  className={`flex-shrink-0 w-8 h-5 rounded-full relative transition-colors ${
                    cfg.enabled ? "bg-green-500" : "bg-gray-600"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      cfg.enabled ? "left-3.5" : "left-0.5"
                    }`}
                  />
                </button>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white truncate">
                    {cfg.provider || cfg.provider}
                    {cfg.modelId && (
                      <span className="text-gray-400 font-normal">
                        {" "}
                        / {cfg.modelId}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <span
                      className={
                        cfg.dataType === "live"
                          ? "text-amber-300"
                          : "text-blue-300"
                      }
                    >
                      {cfg.dataType === "live" ? "Live" : "Forecast"}
                    </span>
                    {cfg.stationId && <span>Station: {cfg.stationId}</span>}
                    {cfg.priority != null && cfg.priority > 0 && (
                      <span>Priority: {cfg.priority}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {onPreview && (
                  <button
                    onClick={() => onPreview(cfg)}
                    className="p-1.5 text-gray-400 hover:text-blue-400 transition-colors"
                    title="Preview wind data"
                  >
                    <EyeIcon className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => onEdit(cfg)}
                  className="p-1.5 text-gray-400 hover:text-white transition-colors"
                  title="Edit"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDelete(cfg)}
                  className="p-1.5 text-gray-400 hover:text-red-400 transition-colors"
                  title="Delete"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <button
        onClick={onAdd}
        className="flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 transition-colors"
      >
        <PlusIcon className="w-4 h-4" />
        Add provider
      </button>
    </div>
  );
}

// ===== Main Page Component =====

const WindProvidersPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>("dashboard");
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // --- Stats ---
  const [stats, setStats] = useState<WindStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // --- Providers list (for modal) ---
  const [providers, setProviders] = useState<any[]>([]);

  // --- Modal ---
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<WindProviderConfig | null>(
    null
  );
  const [modalMode, setModalMode] = useState<"spot" | "country" | "global">(
    "spot"
  );
  const [modalEntityName, setModalEntityName] = useState("");
  const [modalLat, setModalLat] = useState<number | undefined>();
  const [modalLon, setModalLon] = useState<number | undefined>();
  const [modalEntityId, setModalEntityId] = useState<number | null>(null);

  // --- Preview ---
  const [preview, setPreview] = useState<any | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // --- Spot tab ---
  const [spotPage, setSpotPage] = useState(1);
  const [spotLimit, setSpotLimit] = useState(20);
  const [spotSearch, setSpotSearch] = useState("");
  const [spotFilter, setSpotFilter] = useState("all");
  const [spotData, setSpotData] =
    useState<PaginatedResponse<SpotWindEntry> | null>(null);
  const [spotLoading, setSpotLoading] = useState(false);
  const [expandedSpotId, setExpandedSpotId] = useState<number | null>(null);
  const [spotConfigs, setSpotConfigs] = useState<WindProviderConfig[]>([]);
  const [spotConfigsLoading, setSpotConfigsLoading] = useState(false);
  const spotSearchRef = useRef<string>("");
  const spotDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [spotSort, setSpotSort] = useState<{ col: string; asc: boolean }>({ col: "spotName", asc: true });
  const [countrySort, setCountrySort] = useState<{ col: string; asc: boolean }>({ col: "countryName", asc: true });

  // --- Country tab ---
  const [countryPage, setCountryPage] = useState(1);
  const [countryLimit, setCountryLimit] = useState(20);
  const [countrySearch, setCountrySearch] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [countryData, setCountryData] =
    useState<PaginatedResponse<CountryWindEntry> | null>(null);
  const [countryLoading, setCountryLoading] = useState(false);
  const [expandedCountryId, setExpandedCountryId] = useState<number | null>(
    null
  );
  const [countryConfigs, setCountryConfigs] = useState<WindProviderConfig[]>(
    []
  );
  const [countryConfigsLoading, setCountryConfigsLoading] = useState(false);
  const countrySearchRef = useRef<string>("");
  const countryDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // --- Global tab ---
  const [globalConfigs, setGlobalConfigs] = useState<WindProviderConfig[]>([]);
  const [globalLoading, setGlobalLoading] = useState(false);

  // ===== Loaders =====

  const showMessage = useCallback(
    (type: "success" | "error", text: string) => {
      setMessage({ type, text });
      setTimeout(() => setMessage(null), 3000);
    },
    []
  );

  // Sort helper
  function sortItems<T>(items: T[], col: string, asc: boolean): T[] {
    return [...items].sort((a: any, b: any) => {
      const va = a[col] ?? "";
      const vb = b[col] ?? "";
      if (typeof va === "number" && typeof vb === "number") return asc ? va - vb : vb - va;
      return asc ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }

  function SortHeader({ label, col, sort, setSort }: { label: string; col: string; sort: { col: string; asc: boolean }; setSort: (s: { col: string; asc: boolean }) => void }) {
    const active = sort.col === col;
    return (
      <th
        className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-200 select-none"
        onClick={() => setSort({ col, asc: active ? !sort.asc : true })}
      >
        {label} {active ? (sort.asc ? "▲" : "▼") : ""}
      </th>
    );
  }

  // Sorting is now server-side — no client-side sort needed

  // Stats
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await WindProviderService.getStats();
      setStats(data);
    } catch (err) {
      console.error("Failed to load stats:", err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Providers (not needed — provider info comes from config entries)
  const loadProviders = useCallback(async () => {
    // No separate providers endpoint — provider names come from config entries
    setProviders([]);
  }, []);

  // Spots
  const loadSpots = useCallback(async () => {
    setSpotLoading(true);
    try {
      const data = await WindProviderService.getSpotsPaginated(
        spotPage,
        spotLimit,
        spotSearchRef.current || undefined,
        spotFilter,
        spotSort.col,
        spotSort.asc ? "asc" : "desc"
      );
      setSpotData(data);
    } catch (err) {
      console.error("Failed to load spots:", err);
    } finally {
      setSpotLoading(false);
    }
  }, [spotPage, spotLimit, spotFilter, spotSort]);

  // Countries
  const loadCountries = useCallback(async () => {
    setCountryLoading(true);
    try {
      const data = await WindProviderService.getCountriesPaginated(
        countryPage,
        countryLimit,
        countrySearchRef.current || undefined,
        countryFilter,
        countrySort.col,
        countrySort.asc ? "asc" : "desc"
      );
      setCountryData(data);
    } catch (err) {
      console.error("Failed to load countries:", err);
    } finally {
      setCountryLoading(false);
    }
  }, [countryPage, countryLimit, countryFilter, countrySort]);

  // Global (informational only — no separate endpoint)
  const loadGlobal = useCallback(async () => {
    setGlobalLoading(false);
    setGlobalConfigs([]);
  }, []);

  // ===== Effects =====

  // Initial load
  useEffect(() => {
    loadStats();
    loadProviders();
  }, [loadStats, loadProviders]);

  // Spot tab data
  useEffect(() => {
    if (activeTab !== "spot") return;
    loadSpots();
  }, [activeTab, loadSpots]);

  // Spot search debounce
  const handleSpotSearchChange = (value: string) => {
    setSpotSearch(value);
    spotSearchRef.current = value;
    if (spotDebounceRef.current) clearTimeout(spotDebounceRef.current);
    spotDebounceRef.current = setTimeout(() => {
      setSpotPage(1);
      loadSpots();
    }, 500);
  };

  // Country tab data
  useEffect(() => {
    if (activeTab !== "country") return;
    loadCountries();
  }, [activeTab, loadCountries]);

  // Country search debounce
  const handleCountrySearchChange = (value: string) => {
    setCountrySearch(value);
    countrySearchRef.current = value;
    if (countryDebounceRef.current) clearTimeout(countryDebounceRef.current);
    countryDebounceRef.current = setTimeout(() => {
      setCountryPage(1);
      loadCountries();
    }, 500);
  };

  // Global tab data
  useEffect(() => {
    if (activeTab !== "global") return;
    loadGlobal();
  }, [activeTab, loadGlobal]);

  // Cleanup debounce timers
  useEffect(() => {
    return () => {
      if (spotDebounceRef.current) clearTimeout(spotDebounceRef.current);
      if (countryDebounceRef.current)
        clearTimeout(countryDebounceRef.current);
    };
  }, []);

  // ===== Expand handlers =====

  const toggleExpandSpot = async (spotId: number) => {
    if (expandedSpotId === spotId) {
      setExpandedSpotId(null);
      return;
    }
    setExpandedSpotId(spotId);
    setSpotConfigsLoading(true);
    try {
      const configs = await WindProviderService.getSpotConfigs(spotId);
      setSpotConfigs(configs);
    } catch (err) {
      console.error("Failed to load spot configs:", err);
      setSpotConfigs([]);
    } finally {
      setSpotConfigsLoading(false);
    }
  };

  const toggleExpandCountry = async (countryId: number) => {
    if (expandedCountryId === countryId) {
      setExpandedCountryId(null);
      return;
    }
    setExpandedCountryId(countryId);
    setCountryConfigsLoading(true);
    try {
      const configs = await WindProviderService.getCountryConfigs(countryId);
      setCountryConfigs(configs);
    } catch (err) {
      console.error("Failed to load country configs:", err);
      setCountryConfigs([]);
    } finally {
      setCountryConfigsLoading(false);
    }
  };

  // ===== CRUD handlers =====

  // Open modal
  const openAddModal = (
    mode: "spot" | "country" | "global",
    entityId?: number,
    entityName?: string,
    lat?: number,
    lon?: number
  ) => {
    setModalMode(mode);
    setModalEntityId(entityId ?? null);
    setModalEntityName(entityName || "");
    setModalLat(lat);
    setModalLon(lon);
    setModalConfig(null);
    setModalOpen(true);
  };

  const openEditModal = (
    mode: "spot" | "country" | "global",
    config: WindProviderConfig,
    entityId?: number,
    entityName?: string,
    lat?: number,
    lon?: number
  ) => {
    setModalMode(mode);
    setModalEntityId(entityId ?? null);
    setModalEntityName(entityName || "");
    setModalLat(lat);
    setModalLon(lon);
    setModalConfig(config);
    setModalOpen(true);
  };

  // Save config
  const handleSaveConfig = async (config: WindProviderConfig) => {
    try {
      if (modalMode === "spot" && modalEntityId) {
        if (config.id) { await WindProviderService.updateSpotConfig(config.id, config); } else { await WindProviderService.createSpotConfig({ ...config, spotId: modalEntityId }); }
        // Refresh expanded spot
        const configs = await WindProviderService.getSpotConfigs(modalEntityId);
        setSpotConfigs(configs);
        loadSpots();
      } else if (modalMode === "country" && modalEntityId) {
        if (config.id) { await WindProviderService.updateCountryConfig(config.id, config); } else { await WindProviderService.createCountryConfig({ ...config, countryId: modalEntityId }); }
        const configs = await WindProviderService.getCountryConfigs(
          modalEntityId
        );
        setCountryConfigs(configs);
        loadCountries();
      } else if (modalMode === "global") {
        console.log("Global settings are read-only");
        loadGlobal();
      }
      setModalOpen(false);
      showMessage("success", `Provider config ${config.id ? "updated" : "created"} successfully`);
      loadStats();
    } catch (err) {
      console.error("Failed to save config:", err);
      showMessage("error", "Failed to save provider config");
    }
  };

  // Delete config
  const handleDeleteConfig = async (
    mode: "spot" | "country" | "global",
    config: WindProviderConfig,
    entityId?: number
  ) => {
    if (
      !config.id ||
      !window.confirm(
        `Delete ${config.provider || config.provider} config?`
      )
    )
      return;
    try {
      if (mode === "spot" && entityId) {
        await WindProviderService.deleteSpotConfig(config.id!);
        const configs = await WindProviderService.getSpotConfigs(entityId);
        setSpotConfigs(configs);
        loadSpots();
      } else if (mode === "country" && entityId) {
        await WindProviderService.deleteCountryConfig(config.id!);
        const configs = await WindProviderService.getCountryConfigs(entityId);
        setCountryConfigs(configs);
        loadCountries();
      } else if (mode === "global") {
        console.log("Global settings are read-only");
        loadGlobal();
      }
      showMessage("success", "Provider config deleted");
      loadStats();
    } catch (err) {
      console.error("Failed to delete config:", err);
      showMessage("error", "Failed to delete provider config");
    }
  };

  // Toggle config
  const handleToggleConfig = async (
    mode: "spot" | "country" | "global",
    config: WindProviderConfig,
    entityId?: number
  ) => {
    if (!config.id) return;
    try {
      if (mode === "spot" && entityId) {
        await WindProviderService.updateSpotConfig(config.id, { enabled: !config.enabled });
        const configs = await WindProviderService.getSpotConfigs(entityId);
        setSpotConfigs(configs);
      } else if (mode === "country" && entityId) {
        await WindProviderService.updateCountryConfig(config.id, { enabled: !config.enabled });
        const configs = await WindProviderService.getCountryConfigs(entityId);
        setCountryConfigs(configs);
      }
    } catch (err) {
      console.error("Failed to toggle config:", err);
      showMessage("error", "Failed to toggle provider");
    }
  };

  // Preview
  const handlePreview = async (
    config: WindProviderConfig,
    lat?: number,
    lon?: number
  ) => {
    if (!lat || !lon) return;
    setPreviewLoading(true);
    setPreview(null);
    try {
      const data = await WindProviderService.getWindPreview(lat, lon);
      setPreview(data);
    } catch (err) {
      console.error("Preview failed:", err);
    } finally {
      setPreviewLoading(false);
    }
  };

  // ===== Tab content =====

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: <GlobeAltIcon className="w-4 h-4" />,
    },
    {
      id: "spot",
      label: "Spot Providers",
      icon: <MapPinIcon className="w-4 h-4" />,
    },
    {
      id: "country",
      label: "Country Providers",
      icon: <FlagIcon className="w-4 h-4" />,
    },
    {
      id: "global",
      label: "Global Settings",
      icon: <Cog6ToothIcon className="w-4 h-4" />,
    },
  ];

  // ===== Render =====

  return (
    <div className="min-h-full bg-gray-900 py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Wind Providers</h1>
          <p className="text-gray-400 text-sm mt-1">
            Manage wind data providers for spots and countries
          </p>
        </div>
        <button
          onClick={() => {
            loadStats();
            if (activeTab === "spot") loadSpots();
            else if (activeTab === "country") loadCountries();
            else if (activeTab === "global") loadGlobal();
          }}
          disabled={statsLoading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowPathIcon
            className={`w-4 h-4 ${statsLoading ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === "success"
              ? "bg-green-900/50 text-green-300"
              : "bg-red-900/50 text-red-300"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">
              Total Spots
            </div>
            <div className="text-2xl font-bold text-white mt-1">
              {stats.totalSpots.toLocaleString()}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">
              Configured Spots
            </div>
            <div className="text-2xl font-bold text-green-400 mt-1">
              {stats.spotsConfigured.toLocaleString()}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">
              Countries
            </div>
            <div className="text-2xl font-bold text-white mt-1">
              {stats.totalCountries.toLocaleString()}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">
              Configured Countries
            </div>
            <div className="text-2xl font-bold text-green-400 mt-1">
              {stats.countriesConfigured.toLocaleString()}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">
              Live Configs
            </div>
            <div className="text-2xl font-bold text-amber-300 mt-1">
              {stats.liveConfigs}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-gray-400 text-xs uppercase tracking-wide">
              Forecast Configs
            </div>
            <div className="text-2xl font-bold text-blue-300 mt-1">
              {stats.forecastConfigs}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-700 mb-6">
        <nav className="flex gap-1 -mb-px">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-400"
                  : "border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ===== Dashboard Tab ===== */}
      {activeTab === "dashboard" && (
        <div className="space-y-6">
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              Wind Provider Overview
            </h2>
            {stats ? (
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Spot coverage</span>
                    <span className="text-white">
                      {stats.spotsConfigured} / {stats.totalSpots}
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{
                        width: `${
                          stats.totalSpots > 0
                            ? (stats.spotsConfigured / stats.totalSpots) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Country coverage</span>
                    <span className="text-white">
                      {stats.countriesConfigured} / {stats.totalCountries}
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full transition-all"
                      style={{
                        width: `${
                          stats.totalCountries > 0
                            ? (stats.countriesConfigured /
                                stats.totalCountries) *
                              100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-700">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">
                      {stats.totalSpotConfigs}
                    </div>
                    <div className="text-xs text-gray-400">Total Configs</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-300">
                      {stats.liveConfigs}
                    </div>
                    <div className="text-xs text-gray-400">Live Stations</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-300">
                      {stats.forecastConfigs}
                    </div>
                    <div className="text-xs text-gray-400">
                      Forecast Models
                    </div>
                  </div>
                </div>
              </div>
            ) : statsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin" />
                <span className="ml-2 text-gray-400 text-sm">
                  Loading stats...
                </span>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">
                Failed to load statistics
              </p>
            )}
          </div>

          {/* Available providers */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              Supported Providers
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2.5 py-1 rounded text-xs font-medium bg-emerald-900/50 text-emerald-300">openmeteo</span>
                  <span className="px-2 py-0.5 rounded text-xs bg-blue-900/50 text-blue-300">forecast</span>
                </div>
                <p className="text-xs text-gray-400 mb-2">Default forecast provider. 30+ regional models with auto-selection.</p>
                <div className="text-xs text-gray-500 space-y-0.5">
                  <div>AROME-HD <span className="text-gray-600">(1km, France)</span></div>
                  <div>ICON-D2 <span className="text-gray-600">(2km, Germany)</span></div>
                  <div>ICON-EU <span className="text-gray-600">(7km, Europe)</span></div>
                  <div>GFS <span className="text-gray-600">(25km, Global)</span></div>
                </div>
              </div>
              <div className="p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2.5 py-1 rounded text-xs font-medium bg-sky-900/50 text-sky-300">windguru</span>
                  <span className="px-2 py-0.5 rounded text-xs bg-amber-900/50 text-amber-300">live</span>
                  <span className="px-2 py-0.5 rounded text-xs bg-blue-900/50 text-blue-300">forecast</span>
                </div>
                <p className="text-xs text-gray-400 mb-2">Live weather stations + GFS forecast. Requires station ID per spot.</p>
                <div className="text-xs text-gray-500 space-y-0.5">
                  <div>Live stations <span className="text-gray-600">(2-10 min resolution)</span></div>
                  <div>GFS forecast <span className="text-gray-600">(3-hour resolution)</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Spot Providers Tab ===== */}
      {activeTab === "spot" && (
        <div>
          {/* Search + Filter bar */}
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1 min-w-0">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={spotSearch}
                onChange={(e) => handleSpotSearchChange(e.target.value)}
                placeholder="Search spots by name..."
                className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
              />
              {spotLoading && spotSearch && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin" />
                </div>
              )}
            </div>
            <select
              value={spotFilter}
              onChange={(e) => {
                setSpotFilter(e.target.value);
                setSpotPage(1);
              }}
              className="w-48 shrink-0 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Spots</option>
              <option value="configured">Configured</option>
              <option value="unconfigured">Unconfigured</option>
              <option value="live">With Live</option>
            </select>
          </div>

          {/* Table */}
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            {spotLoading && !spotData ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin" />
                <span className="ml-3 text-gray-400">Loading spots...</span>
              </div>
            ) : spotData && spotData.items.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <SortHeader label="Spot" col="spotName" sort={spotSort} setSort={setSpotSort} />
                    <SortHeader label="Country" col="countryName" sort={spotSort} setSort={setSpotSort} />
                    <SortHeader label="Providers" col="providerCount" sort={spotSort} setSort={setSpotSort} />
                    <SortHeader label="Sessions" col="totalSessions" sort={spotSort} setSort={setSpotSort} />
                    <th className="w-10 px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {spotData.items.map((spot) => (
                    <React.Fragment key={spot.spotId}>
                      <tr
                        onClick={() => toggleExpandSpot(spot.spotId)}
                        className="cursor-pointer hover:bg-gray-750 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <span className="mr-2">
                            {countryFlag(spot.alpha2)}
                          </span>
                          <span className="font-medium text-white">
                            {spot.spotName}
                          </span>
                          {spot.latitude != null && (
                            <span className="text-xs text-gray-500 ml-2">
                              {spot.latitude.toFixed(2)},{" "}
                              {spot.longitude.toFixed(2)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300">
                          {spot.countryName}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {spot.providerCount > 0 ? (
                            <span className="text-green-400">
                              {"● "}
                              {spot.providerCount} provider
                              {spot.providerCount > 1 ? "s" : ""}
                            </span>
                          ) : (
                            <span className="text-gray-500">
                              {"○ "}Default only
                            </span>
                          )}
                          {spot.liveCount > 0 && (
                            <span className="text-amber-300 text-xs ml-1">
                              (Live)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-400">
                          {(spot.totalSessions || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <ChevronIcon
                            rotated={expandedSpotId === spot.spotId}
                          />
                        </td>
                      </tr>
                      {expandedSpotId === spot.spotId && (
                        <tr>
                          <td colSpan={5} className="bg-gray-900/50">
                            <ConfigCards
                              configs={spotConfigs}
                              loading={spotConfigsLoading}
                              onAdd={() =>
                                openAddModal(
                                  "spot",
                                  spot.spotId,
                                  spot.spotName,
                                  spot.latitude,
                                  spot.longitude
                                )
                              }
                              onEdit={(cfg) =>
                                openEditModal(
                                  "spot",
                                  cfg,
                                  spot.spotId,
                                  spot.spotName,
                                  spot.latitude,
                                  spot.longitude
                                )
                              }
                              onDelete={(cfg) =>
                                handleDeleteConfig("spot", cfg, spot.spotId)
                              }
                              onToggle={(cfg) =>
                                handleToggleConfig("spot", cfg, spot.spotId)
                              }
                              onPreview={(cfg) =>
                                handlePreview(
                                  cfg,
                                  spot.latitude,
                                  spot.longitude
                                )
                              }
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12 text-gray-400">
                {spotSearch
                  ? `No spots found matching "${spotSearch}"`
                  : "No spots available"}
              </div>
            )}
          </div>

          {/* Pagination */}
          {spotData && spotData.totalPages > 1 && (
            <Pagination
              darkMode
              currentPage={spotData.page}
              totalPages={spotData.totalPages}
              totalItems={spotData.totalItems}
              limit={spotData.limit}
              onPageChange={setSpotPage}
              onLimitChange={(newLimit) => {
                setSpotLimit(newLimit);
                setSpotPage(1);
              }}
              limitOptions={[20, 50, 100, 250, 500, 1000]}
            />
          )}
        </div>
      )}

      {/* ===== Country Providers Tab ===== */}
      {activeTab === "country" && (
        <div>
          {/* Search + Filter bar */}
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1 min-w-0">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={countrySearch}
                onChange={(e) => handleCountrySearchChange(e.target.value)}
                placeholder="Search countries by name..."
                className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
              />
              {countryLoading && countrySearch && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin" />
                </div>
              )}
            </div>
            <select
              value={countryFilter}
              onChange={(e) => {
                setCountryFilter(e.target.value);
                setCountryPage(1);
              }}
              className="w-48 shrink-0 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Countries</option>
              <option value="configured">Configured</option>
              <option value="unconfigured">Unconfigured</option>
            </select>
          </div>

          {/* Table */}
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            {countryLoading && !countryData ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin" />
                <span className="ml-3 text-gray-400">
                  Loading countries...
                </span>
              </div>
            ) : countryData && countryData.items.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <SortHeader label="Country" col="countryName" sort={countrySort} setSort={setCountrySort} />
                    <SortHeader label="Spots" col="spotCount" sort={countrySort} setSort={setCountrySort} />
                    <SortHeader label="Config" col="providerCount" sort={countrySort} setSort={setCountrySort} />
                    <th className="w-28 text-center text-xs font-medium text-gray-400 uppercase tracking-wide px-4 py-3">
                      Actions
                    </th>
                    <th className="w-10 px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {countryData.items.map((country) => (
                    <React.Fragment key={country.countryId}>
                      <tr
                        onClick={() =>
                          toggleExpandCountry(country.countryId)
                        }
                        className="cursor-pointer hover:bg-gray-750 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <span className="mr-2">
                            {countryFlag(country.alpha2)}
                          </span>
                          <span className="font-medium text-white">
                            {country.countryName}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-400">
                          {country.spotCount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {country.configured ? (
                            <span className="text-green-400">
                              {"● "}{country.models}
                            </span>
                          ) : (
                            <span className="text-gray-500">
                              {"○ "}No config
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {country.configured ? (
                            <div className="flex justify-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpandCountry(country.countryId);
                                }}
                                className="px-2 py-1 text-xs text-gray-300 hover:text-white transition-colors"
                              >
                                Edit
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openAddModal(
                                  "country",
                                  country.countryId,
                                  country.countryName
                                );
                              }}
                              className="flex items-center gap-1 px-2 py-1 text-xs text-blue-400 hover:text-blue-300 transition-colors mx-auto"
                            >
                              <PlusIcon className="w-3 h-3" />
                              Add
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <ChevronIcon
                            rotated={expandedCountryId === country.countryId}
                          />
                        </td>
                      </tr>
                      {expandedCountryId === country.countryId && (
                        <tr>
                          <td colSpan={5} className="bg-gray-900/50">
                            <ConfigCards
                              configs={countryConfigs}
                              loading={countryConfigsLoading}
                              onAdd={() =>
                                openAddModal(
                                  "country",
                                  country.countryId,
                                  country.countryName
                                )
                              }
                              onEdit={(cfg) =>
                                openEditModal(
                                  "country",
                                  cfg,
                                  country.countryId,
                                  country.countryName
                                )
                              }
                              onDelete={(cfg) =>
                                handleDeleteConfig(
                                  "country",
                                  cfg,
                                  country.countryId
                                )
                              }
                              onToggle={(cfg) =>
                                handleToggleConfig(
                                  "country",
                                  cfg,
                                  country.countryId
                                )
                              }
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12 text-gray-400">
                {countrySearch
                  ? `No countries found matching "${countrySearch}"`
                  : "No countries available"}
              </div>
            )}
          </div>

          {/* Pagination */}
          {countryData && countryData.totalPages > 1 && (
            <Pagination
              darkMode
              currentPage={countryData.page}
              totalPages={countryData.totalPages}
              totalItems={countryData.totalItems}
              limit={countryData.limit}
              onPageChange={setCountryPage}
              onLimitChange={(newLimit) => {
                setCountryLimit(newLimit);
                setCountryPage(1);
              }}
              limitOptions={[20, 50, 100, 250, 500, 1000]}
            />
          )}
        </div>
      )}

      {/* ===== Global Settings Tab ===== */}
      {activeTab === "global" && (
        <div>
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-white">
              Global Default Settings
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              These defaults apply when no spot or country-level config exists. They are system-level and not editable from here.
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg overflow-hidden">
            {false ? (
              <div className="divide-y divide-gray-700">
                {globalConfigs.map((cfg) => (
                  <div
                    key={cfg.id}
                    className="flex items-center justify-between p-4 hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <span
                        className={`px-2.5 py-1 rounded text-xs font-medium ${
                          cfg.dataType === "live"
                            ? "bg-amber-900/50 text-amber-300"
                            : "bg-blue-900/50 text-blue-300"
                        }`}
                      >
                        {cfg.dataType}
                      </span>
                      <div>
                        <div className="text-sm font-medium text-white">
                          {cfg.provider || cfg.provider}
                          {cfg.modelId && (
                            <span className="text-gray-400 font-normal">
                              {" "}
                              / {cfg.modelId}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          Priority: {cfg.priority ?? 0}
                          {cfg.enabled ? (
                            <span className="text-green-400 ml-2">
                              Enabled
                            </span>
                          ) : (
                            <span className="text-red-400 ml-2">
                              Disabled
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditModal("global", cfg)}
                        className="p-2 text-gray-400 hover:text-white transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteConfig("global", cfg)}
                        className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-white mb-2">Default Forecast Provider</h3>
                  <p className="text-sm text-gray-400 mb-3">Open-Meteo is the default forecast provider for all spots. Auto-selects the best regional model based on location.</p>
                  <div className="bg-gray-900 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-2.5 py-1 rounded text-xs font-medium bg-emerald-900/50 text-emerald-300">openmeteo</span>
                      <span className="px-2.5 py-1 rounded text-xs font-medium bg-blue-900/50 text-blue-300">forecast</span>
                      <span className="text-green-400 text-xs">Active on all 10,113 spots</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-2">Model Auto-Selection Rules</h3>
                  <p className="text-sm text-gray-400 mb-3">When no specific model is configured, the system auto-selects based on location:</p>
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Region</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Model</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Resolution</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/50">
                      <tr><td className="px-4 py-2 text-sm text-white">France</td><td className="px-4 py-2 text-sm"><span className="px-2 py-0.5 rounded text-xs bg-blue-900/40 text-blue-300">AROME-HD</span></td><td className="px-4 py-2 text-sm text-gray-400">1km</td></tr>
                      <tr><td className="px-4 py-2 text-sm text-white">Germany</td><td className="px-4 py-2 text-sm"><span className="px-2 py-0.5 rounded text-xs bg-blue-900/40 text-blue-300">ICON-D2</span></td><td className="px-4 py-2 text-sm text-gray-400">2km</td></tr>
                      <tr><td className="px-4 py-2 text-sm text-white">Europe</td><td className="px-4 py-2 text-sm"><span className="px-2 py-0.5 rounded text-xs bg-blue-900/40 text-blue-300">ICON-EU</span></td><td className="px-4 py-2 text-sm text-gray-400">7km</td></tr>
                      <tr><td className="px-4 py-2 text-sm text-white">Global</td><td className="px-4 py-2 text-sm"><span className="px-2 py-0.5 rounded text-xs bg-blue-900/40 text-blue-300">GFS</span></td><td className="px-4 py-2 text-sm text-gray-400">25km</td></tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-2">Resolution Priority</h3>
                  <p className="text-sm text-gray-400">Spot config → Country config → Auto-select by location. Higher resolution models are preferred when available for a given location.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Config Modal */}
      <ConfigModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveConfig}
        config={modalConfig}
        providers={providers}
        mode={modalMode}
        entityName={modalEntityName}
        latitude={modalLat}
        longitude={modalLon}
      />

      {/* Wind Preview Panel */}
      <PreviewPanel
        preview={preview}
        loading={previewLoading}
        onClose={() => {
          setPreview(null);
          setPreviewLoading(false);
        }}
      />
    </div>
  );
};

export default WindProvidersPage;
